import { useEffect, useRef, type MouseEvent } from "react";
import { ChevronRight, Plus } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import jeniferPortrait from "@/assets/jenifer-portrait-480.webp";
import { getBlogSummary, postsForRoute, useBlogPost } from "@/blog/data";
import { formatBlogDate, sameMadridDay } from "@/blog/format";
import { markArticleRead, useReadArticles } from "@/blog/readHistory";
import { BLOG_ROUTE_BY_ID, blogPostPath, blogRoutePath } from "@/blog/routes";
import type { BlogPost } from "@/blog/types";
import { ArticleToc } from "@/components/blog/ArticleToc";
import { BlogCard } from "@/components/blog/BlogCard";
import { BlogCover } from "@/components/blog/BlogCover";
import { BlogCtaBand } from "@/components/blog/BlogCtaBand";
import { FreeAuditButton } from "@/components/ui/Cta";
import { usePageSeo } from "@/hooks/usePageSeo";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { trackEvent } from "@/utils/analytics";

export function BlogPostPage() {
  const { slug = "" } = useParams();
  const { post, loading } = useBlogPost(slug);

  if (post) return <Article key={post.slug} post={post} />;
  if (loading || getBlogSummary(slug)) {
    return <main id="main-content" tabIndex={-1} aria-busy="true" className="min-h-screen outline-none" />;
  }
  return <NotFoundPage />;
}

function Article({ post }: { post: BlogPost }) {
  const path = blogPostPath(post.slug);
  usePageSeo(path, undefined, post);
  const navigate = useNavigate();
  const route = BLOG_ROUTE_BY_ID[post.route];
  const routePosts = postsForRoute(post.route);
  const position = routePosts.findIndex((item) => item.slug === post.slug);
  const next = position >= 0 ? routePosts[position + 1] : undefined;
  const related = routePosts.filter((item) => item.slug !== post.slug && item.slug !== next?.slug).slice(0, 3);
  const read = useReadArticles();
  const endRef = useRef<HTMLDivElement>(null);
  const updated = !sameMadridDay(post.publishedAt, post.updatedAt);

  /* Reaching the end of the body counts as reading the article. */
  useEffect(() => {
    const el = endRef.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      markArticleRead(post.slug);
      trackEvent("article_read", { slug: post.slug, route: post.route });
    });
    io.observe(el);
    return () => io.disconnect();
  }, [post.slug, post.route]);

  /* Links inside the article stay in the app (no full reload) and count as clicks. */
  function onBodyClick(event: MouseEvent<HTMLDivElement>) {
    const anchor = (event.target as Element).closest("a");
    const href = anchor?.getAttribute("href");
    if (!anchor || !href) return;
    if (href.startsWith("/") && !href.startsWith("//")) {
      trackEvent("cta_click", { location: "article_body", target: href, label: anchor.textContent?.trim().slice(0, 80) });
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      navigate(href);
    }
  }

  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <article aria-labelledby="page-heading">
        <header className="light-pool relative overflow-hidden [--pool-x:85%] [--pool-y:10%]">
          <div className="mx-auto max-w-6xl px-4 pb-10 pt-28 sm:px-6 sm:pt-32 lg:px-8">
            <nav aria-label="Ruta de navegación" className="hero-rise">
              <ol className="flex flex-wrap items-center gap-1.5 text-sm text-ink-muted">
                <li>
                  <Link to="/" className="rounded transition-colors hover:text-brand-primary">
                    Inicio
                  </Link>
                </li>
                <li aria-hidden="true">
                  <ChevronRight className="size-3.5" />
                </li>
                <li>
                  <Link to="/blog" className="rounded transition-colors hover:text-brand-primary">
                    Blog
                  </Link>
                </li>
                <li aria-hidden="true">
                  <ChevronRight className="size-3.5" />
                </li>
                <li>
                  <Link to={blogRoutePath(post.route)} className="rounded font-medium text-brand-primary transition-colors hover:text-teal-strong">
                    Ruta {route.label}
                  </Link>
                </li>
              </ol>
            </nav>

            <h1
              id="page-heading"
              className="hero-rise mt-6 max-w-4xl font-display text-[clamp(2rem,4vw,3.25rem)] font-semibold leading-[1.05] tracking-[-0.028em] text-brand-primary"
              style={{ ["--delay" as string]: "60ms" }}
            >
              {post.title}
            </h1>

            <div
              className="hero-rise mt-7 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between"
              style={{ ["--delay" as string]: "120ms" }}
            >
              <div className="flex items-center gap-3.5">
                <img
                  src={jeniferPortrait}
                  alt=""
                  width={44}
                  height={44}
                  className="size-11 shrink-0 rounded-full object-cover object-top ring-2 ring-white"
                />
                <p className="text-sm leading-relaxed text-ink-muted">
                  <Link to="/#sobre-mi" className="font-semibold text-brand-primary transition-colors hover:text-teal-strong">
                    Jenifer Roelstraete
                  </Link>{" "}
                  · Estratega digital
                  <br />
                  {updated ? "Actualizado el " : "Publicado el "}
                  <time dateTime={updated ? post.updatedAt : post.publishedAt}>
                    {formatBlogDate(updated ? post.updatedAt : post.publishedAt)}
                  </time>{" "}
                  · {post.readingMinutes} min de lectura
                </p>
              </div>
              {position >= 0 && routePosts.length > 1 ? (
                <Link
                  to={blogRoutePath(post.route)}
                  className="inline-flex w-fit items-center gap-2 rounded-full bg-teal-soft px-3.5 py-1.5 text-sm font-semibold text-teal-strong transition-colors hover:bg-brand-secondary hover:text-brand-primary"
                >
                  <span aria-hidden="true" className="size-1.5 rounded-full bg-brand-secondary" />
                  Ruta {route.label} · Paso {position + 1} de {routePosts.length}
                </Link>
              ) : null}
            </div>

            {post.coverUrl ? (
              <div
                className="hero-rise mt-10 aspect-[16/9] overflow-hidden rounded-[1.75rem] shadow-lit-lg"
                style={{ ["--delay" as string]: "180ms" }}
              >
                <BlogCover post={post} priority sizes="(min-width: 1152px) 1088px, 100vw" />
              </div>
            ) : null}
          </div>
        </header>

        <div className="mx-auto grid max-w-6xl gap-12 px-4 pb-20 pt-6 sm:px-6 lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-16 lg:px-8 md:pb-28">
          <aside className="hidden lg:block">
            <div className="sticky top-[calc(var(--banner-h,0px)+7rem)] space-y-5">
              {post.headings.some((heading) => heading.level === 2) ? (
                <div className="rounded-2xl border border-brand-primary/8 bg-white p-5 shadow-lit">
                  <p className="mb-3 text-sm font-semibold text-brand-primary">En este artículo</p>
                  <ArticleToc headings={post.headings} label="Índice del artículo" />
                </div>
              ) : null}
              {position >= 0 && routePosts.length > 1 ? (
                <div className="rounded-2xl border border-brand-primary/8 bg-white p-5 shadow-lit">
                  <p className="text-sm font-semibold text-brand-primary">Ruta {route.label}</p>
                  <div aria-hidden="true" className="mt-3 flex gap-1.5">
                    {routePosts.map((item, i) => (
                      <span
                        key={item.slug}
                        className={`h-1.5 flex-1 rounded-full ${
                          i <= position || read.has(item.slug) ? "bg-brand-secondary" : "bg-brand-primary/10"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="mt-3 text-sm leading-snug text-ink-muted">
                    Paso {position + 1} de {routePosts.length}
                    {next ? ` · Siguiente: ${next.title}` : " · Último paso"}
                  </p>
                </div>
              ) : null}
              <FreeAuditButton location="article_sidebar" variant="primary" size="md" label="Auditoría gratuita" className="w-full justify-center" />
            </div>
          </aside>

          <div className="min-w-0">
            {post.quickAnswer ? (
              <section
                aria-labelledby="quick-answer"
                className="rounded-[1.75rem] border border-brand-secondary/50 bg-teal-soft px-6 py-6 sm:px-8"
              >
                <h2 id="quick-answer" className="text-sm font-bold text-teal-strong">
                  En resumen
                </h2>
                <p className="mt-2 whitespace-pre-line text-lg leading-relaxed text-brand-primary">{post.quickAnswer}</p>
              </section>
            ) : null}

            {post.headings.some((heading) => heading.level === 2) ? (
              <details className="group mt-6 rounded-2xl border border-brand-primary/10 bg-white lg:hidden">
                <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-4 px-5 py-3 text-sm font-semibold text-brand-primary [&::-webkit-details-marker]:hidden">
                  En este artículo
                  <Plus aria-hidden="true" className="size-4 transition-transform duration-300 group-open:rotate-45" />
                </summary>
                <div className="px-5 pb-4">
                  <ArticleToc headings={post.headings} label="Índice del artículo" />
                </div>
              </details>
            ) : null}

            <div
              className="article-body mt-10 max-w-[42rem]"
              onClick={onBodyClick}
              dangerouslySetInnerHTML={{ __html: post.html }}
            />
            <div ref={endRef} aria-hidden="true" />

            {post.faqs.length ? (
              <section aria-labelledby="article-faq" className="mt-16 max-w-[42rem]">
                <h2
                  id="article-faq"
                  className="font-display text-2xl sm:text-3xl font-semibold leading-tight tracking-[-0.02em] text-brand-primary"
                >
                  Preguntas frecuentes
                </h2>
                <div className="mt-5 border-t border-brand-primary/12">
                  {post.faqs.map((faq) => (
                    <details key={faq.question} className="group border-b border-brand-primary/12">
                      <summary className="group/q flex min-h-14 cursor-pointer list-none items-center justify-between gap-6 py-4 text-left [&::-webkit-details-marker]:hidden">
                        <h3 className="text-lg font-semibold leading-snug text-brand-primary transition-colors duration-300 group-hover/q:text-teal-strong">
                          {faq.question}
                        </h3>
                        <span
                          aria-hidden="true"
                          className="grid size-8 shrink-0 place-items-center rounded-full border border-brand-primary/15 text-brand-primary transition-[rotate,background-color,border-color] duration-300 group-hover/q:border-brand-secondary group-hover/q:bg-teal-soft group-open:rotate-45 group-open:border-brand-secondary group-open:bg-brand-secondary"
                        >
                          <Plus className="size-4" />
                        </span>
                      </summary>
                      <p className="whitespace-pre-line pb-6 pr-10 text-lg leading-relaxed text-ink-soft">{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            ) : null}

            <section
              aria-label="Sobre la autora"
              className="light-pool-navy mt-16 flex max-w-[42rem] flex-col gap-5 rounded-[1.75rem] bg-brand-primary px-6 py-7 text-white [--pool-x:10%] [--pool-y:0%] sm:flex-row sm:items-center sm:px-8"
            >
              <img
                src={jeniferPortrait}
                alt="Jenifer Roelstraete"
                width={76}
                height={76}
                loading="lazy"
                className="size-[4.75rem] shrink-0 rounded-full object-cover object-top ring-[3px] ring-brand-secondary"
              />
              <div>
                <p className="font-display text-xl font-semibold">Escrito por Jenifer Roelstraete</p>
                <p className="mt-1.5 leading-relaxed text-on-navy-muted">
                  Fundadora de Lucentia Social. Ayuda a negocios que trabajan con cita a conseguir más reservas con Instagram, web y automatización.
                </p>
              </div>
            </section>

            {next ? (
              <Link
                to={blogPostPath(next.slug)}
                className="group/next mt-6 flex max-w-[42rem] items-center justify-between gap-6 rounded-[1.75rem] border border-dashed border-brand-primary/25 px-6 py-5 transition-[border-color,background-color] duration-300 hover:border-brand-secondary hover:bg-white"
              >
                <span>
                  <span className="block text-sm text-ink-muted">Siguiente en la Ruta {route.label}</span>
                  <span className="mt-1 block font-display text-xl font-semibold leading-snug text-brand-primary">{next.title}</span>
                </span>
                <span
                  aria-hidden="true"
                  className="grid size-11 shrink-0 place-items-center rounded-full bg-brand-secondary text-brand-primary transition-transform duration-300 group-hover/next:translate-x-1"
                >
                  <ChevronRight className="size-5" />
                </span>
              </Link>
            ) : null}
          </div>
        </div>
      </article>

      <BlogCtaBand cta={post.cta} location="article_end" />

      {related.length ? (
        <section aria-labelledby="related-heading" className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 md:pb-32 lg:px-8">
          <h2
            id="related-heading"
            className="font-display text-3xl font-semibold leading-tight tracking-[-0.02em] text-brand-primary"
          >
            Más de la Ruta {route.label}
          </h2>
          <ul className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <li key={item.slug}>
                <BlogCard post={item} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </main>
  );
}
